import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getStaff,
  getSalaryHistory, addSalaryRecord, deleteSalaryRecord,
  getLeaveAdjustments, addLeaveAdjustment, deleteLeaveAdjustment,
  listPayrollRuns, generatePayrollRun, getPayrollRun, updatePayslip, finalizePayrollRun,
  getMyPayslips,
} from '../api';
import { formatRupeesUI, formatDateDdMmmYy } from '../utils/format';

interface StaffLite { id: number; staffName: string; isPartner: boolean; isActive: boolean; }

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type Tab = 'runs' | 'salaries' | 'leave-balances' | 'my-payslips';

const Payroll: React.FC = () => {
  const { isAdmin, isHR, isPartner } = useAuth();
  const canManage = isAdmin || isHR || isPartner;
  const [tab, setTab] = useState<Tab>(canManage ? 'runs' : 'my-payslips');

  const tabs: { key: Tab; label: string }[] = canManage
    ? [
        { key: 'runs', label: '📅 Payroll Runs' },
        { key: 'salaries', label: '💰 Salaries' },
        { key: 'leave-balances', label: '🏖️ Leave Balances' },
        { key: 'my-payslips', label: '🧾 My Payslips' },
      ]
    : [{ key: 'my-payslips', label: '🧾 My Payslips' }];

  return (
    <div className="space-y-0">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Payroll</h2>
        <p className="text-sm text-gray-500 mt-1">Salary, leave balances, and monthly payroll runs</p>
      </div>

      {tabs.length > 1 && (
        <div className="flex gap-1 border-b border-gray-200 mb-6">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {tab === 'runs' && canManage && <RunsTab />}
      {tab === 'salaries' && canManage && <SalariesTab />}
      {tab === 'leave-balances' && canManage && <LeaveBalancesTab />}
      {tab === 'my-payslips' && <MyPayslipsTab />}
    </div>
  );
};

// ── PAYROLL RUNS ─────────────────────────────────────────────────────────────

interface Payslip {
  id: number; staffId: number; staff: { id: number; staffName: string; email?: string };
  monthlyGross: string; daysInMonth: number; payableDays: number; lopDays: number;
  proratedGross: string; adjustments: { label: string; amount: number }[]; netPay: string; remarks?: string | null;
}
interface Run {
  id: number; month: number; year: number; status: 'DRAFT' | 'FINALIZED';
  finalizedAt?: string | null; payslips?: Payslip[]; _count?: { payslips: number };
}

const RunsTab: React.FC = () => {
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [openRun, setOpenRun] = useState<Run | null>(null);
  const now = new Date();
  const [genMonth, setGenMonth] = useState(now.getMonth() + 1);
  const [genYear, setGenYear] = useState(now.getFullYear());
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const fetchRuns = async () => {
    setLoading(true);
    try {
      const res = await listPayrollRuns();
      setRuns(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRuns(); }, []);

  const openRunDetail = async (id: number) => {
    const res = await getPayrollRun(id);
    setOpenRun(res.data);
  };

  const handleGenerate = async () => {
    setError('');
    setGenerating(true);
    try {
      const res = await generatePayrollRun(genMonth, genYear);
      await fetchRuns();
      setOpenRun(res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate run');
    } finally {
      setGenerating(false);
    }
  };

  if (openRun) {
    return <RunDetail run={openRun} onBack={() => { setOpenRun(null); fetchRuns(); }} onRefresh={() => openRunDetail(openRun.id)} />;
  }

  return (
    <div className="space-y-6">
      <div className="card p-4">
        <h3 className="font-semibold text-gray-800 mb-3">Generate / Open a Run</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">Month</label>
            <select className="input-field" value={genMonth} onChange={(e) => setGenMonth(Number(e.target.value))}>
              {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Year</label>
            <input type="number" className="input-field w-28" value={genYear} onChange={(e) => setGenYear(Number(e.target.value))} />
          </div>
          <button className="btn-primary" disabled={generating} onClick={handleGenerate}>
            {generating ? 'Generating…' : 'Generate / Refresh Draft'}
          </button>
        </div>
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
        <p className="text-xs text-gray-400 mt-2">Regenerating a DRAFT run recomputes attendance/leave-derived figures and the automatic TDS line, but keeps manually added adjustment lines. Net pay is final only once the run is finalized — payments can only be made from finalized runs.</p>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="table-cell text-left">Month</th>
              <th className="table-cell text-left">Status</th>
              <th className="table-cell text-left">Staff</th>
              <th className="table-cell text-left">Finalized</th>
              <th className="table-cell text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="table-cell text-center text-gray-400 py-6">Loading…</td></tr>}
            {!loading && runs.length === 0 && <tr><td colSpan={5} className="table-cell text-center text-gray-400 py-6">No payroll runs yet</td></tr>}
            {runs.map((r) => (
              <tr key={r.id} className="border-t border-gray-100">
                <td className="table-cell font-medium">{MONTH_NAMES[r.month - 1]} {r.year}</td>
                <td className="table-cell">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.status === 'FINALIZED' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {r.status}
                  </span>
                </td>
                <td className="table-cell">{r._count?.payslips ?? '—'}</td>
                <td className="table-cell text-gray-500">{r.finalizedAt ? formatDateDdMmmYy(r.finalizedAt) : '—'}</td>
                <td className="table-cell">
                  <button className="text-blue-600 hover:underline text-sm" onClick={() => openRunDetail(r.id)}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RunDetail: React.FC<{ run: Run; onBack: () => void; onRefresh: () => void }> = ({ run, onBack, onRefresh }) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [lopDraft, setLopDraft] = useState('');
  const [adjDraft, setAdjDraft] = useState<{ label: string; amount: number }[]>([]);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const isDraft = run.status === 'DRAFT';

  const startEdit = (p: Payslip) => {
    setEditingId(p.id);
    setLopDraft(String(p.lopDays));
    setAdjDraft(p.adjustments || []);
  };

  const saveEdit = async (p: Payslip) => {
    setSaving(true);
    try {
      await updatePayslip(run.id, p.id, { lopDays: Number(lopDraft), adjustments: adjDraft });
      setEditingId(null);
      onRefresh();
    } finally {
      setSaving(false);
    }
  };

  const addAdjLine = () => setAdjDraft((d) => [...d, { label: '', amount: 0 }]);
  const removeAdjLine = (i: number) => setAdjDraft((d) => d.filter((_, idx) => idx !== i));

  const handleFinalize = async () => {
    if (!window.confirm(`Finalize payroll for ${MONTH_NAMES[run.month - 1]} ${run.year}? This locks every payslip and cannot be undone.`)) return;
    setFinalizing(true);
    try {
      await finalizePayrollRun(run.id);
      onRefresh();
    } finally {
      setFinalizing(false);
    }
  };

  const totalNet = (run.payslips || []).reduce((s, p) => s + Number(p.netPay), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button className="text-blue-600 hover:underline text-sm" onClick={onBack}>← Back to Runs</button>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${run.status === 'FINALIZED' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {run.status}
          </span>
          {isDraft && (
            <button className="btn-primary" disabled={finalizing} onClick={handleFinalize}>
              {finalizing ? 'Finalizing…' : 'Finalize Run'}
            </button>
          )}
        </div>
      </div>

      <h3 className="text-lg font-semibold text-gray-800">{MONTH_NAMES[run.month - 1]} {run.year} — {(run.payslips || []).length} staff, ₹{formatRupeesUI(totalNet)} total net</h3>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="table-cell text-left">Staff</th>
              <th className="table-cell text-right">Monthly Gross</th>
              <th className="table-cell text-right">Payable Days</th>
              <th className="table-cell text-right">LOP Days</th>
              <th className="table-cell text-right">Prorated Gross</th>
              <th className="table-cell text-left">Adjustments</th>
              <th className="table-cell text-right">Net Pay</th>
              {isDraft && <th className="table-cell text-left">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {(run.payslips || []).map((p) => {
              const isEditing = editingId === p.id;
              return (
                <tr key={p.id} className="border-t border-gray-100 align-top">
                  <td className="table-cell font-medium">{p.staff.staffName}</td>
                  <td className="table-cell text-right">₹{formatRupeesUI(p.monthlyGross)}</td>
                  <td className="table-cell text-right">{p.payableDays} / {p.daysInMonth}</td>
                  <td className="table-cell text-right">
                    {isEditing ? (
                      <input type="number" min={0} className="input-field w-20 text-right" value={lopDraft} onChange={(e) => setLopDraft(e.target.value)} />
                    ) : p.lopDays}
                  </td>
                  <td className="table-cell text-right">₹{formatRupeesUI(p.proratedGross)}</td>
                  <td className="table-cell">
                    {isEditing ? (
                      <div className="space-y-1">
                        {adjDraft.map((a, i) => (
                          <div key={i} className="flex gap-1 items-center">
                            <input className="input-field text-xs" placeholder="Label" value={a.label}
                              onChange={(e) => setAdjDraft((d) => d.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} />
                            <input type="number" className="input-field w-20 text-xs" placeholder="Amount" value={a.amount}
                              onChange={(e) => setAdjDraft((d) => d.map((x, idx) => idx === i ? { ...x, amount: Number(e.target.value) } : x))} />
                            <button className="text-red-500 text-xs" onClick={() => removeAdjLine(i)}>✕</button>
                          </div>
                        ))}
                        <button className="text-blue-600 text-xs hover:underline" onClick={addAdjLine}>+ Add line</button>
                      </div>
                    ) : (
                      (p.adjustments || []).length === 0 ? <span className="text-gray-400">—</span> : (
                        <div className="text-xs space-y-0.5">
                          {p.adjustments.map((a, i) => (
                            <div key={i} className={a.amount < 0 ? 'text-red-600' : 'text-green-600'}>
                              {a.label}: {a.amount >= 0 ? '+' : ''}₹{formatRupeesUI(a.amount)}
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </td>
                  <td className="table-cell text-right font-semibold">₹{formatRupeesUI(p.netPay)}</td>
                  {isDraft && (
                    <td className="table-cell">
                      {isEditing ? (
                        <div className="flex gap-2">
                          <button className="text-green-600 text-xs font-medium hover:underline" disabled={saving} onClick={() => saveEdit(p)}>Save</button>
                          <button className="text-gray-500 text-xs hover:underline" onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                      ) : (
                        <button className="text-blue-600 text-xs hover:underline" onClick={() => startEdit(p)}>Edit</button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── SALARIES ─────────────────────────────────────────────────────────────────

interface SalaryRecord { id: number; effectiveFrom: string; monthlyGross: string; monthlyTds?: string; notes?: string | null; createdBy?: { staffName: string } | null; createdAt: string }

const SalariesTab: React.FC = () => {
  const [staffList, setStaffList] = useState<StaffLite[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [history, setHistory] = useState<SalaryRecord[]>([]);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [monthlyGross, setMonthlyGross] = useState('');
  const [monthlyTds, setMonthlyTds] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getStaff().then((res) => {
      const eligible = (res.data as StaffLite[]).filter((s) => !s.isPartner);
      setStaffList(eligible);
      if (eligible.length > 0) setSelectedId(eligible[0].id);
    });
  }, []);

  useEffect(() => {
    if (selectedId) loadHistory(selectedId);
  }, [selectedId]);

  const loadHistory = async (staffId: number) => {
    const res = await getSalaryHistory(staffId);
    setHistory(res.data);
  };

  const handleAdd = async () => {
    if (!selectedId) return;
    setError('');
    const gross = Number(monthlyGross);
    const tds = monthlyTds === '' ? 0 : Number(monthlyTds);
    if (!effectiveFrom || !(gross >= 0)) { setError('Effective date and a valid monthly gross are required'); return; }
    if (!(tds >= 0) || tds > gross) { setError('TDS must be between 0 and the monthly gross'); return; }
    setSaving(true);
    try {
      await addSalaryRecord(selectedId, { effectiveFrom, monthlyGross: gross, monthlyTds: tds, notes: notes || undefined });
      setEffectiveFrom(''); setMonthlyGross(''); setMonthlyTds(''); setNotes('');
      loadHistory(selectedId);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add revision');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this salary record?')) return;
    try {
      await deleteSalaryRecord(id);
      if (selectedId) loadHistory(selectedId);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Cannot delete');
    }
  };

  const current = history[0];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="card p-4">
        <h3 className="font-semibold text-gray-800 mb-3">Staff</h3>
        <div className="space-y-1 max-h-[500px] overflow-y-auto">
          {staffList.map((s) => (
            <button key={s.id} onClick={() => setSelectedId(s.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm ${selectedId === s.id ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50 text-gray-700'} ${!s.isActive ? 'opacity-50' : ''}`}>
              {s.staffName}{!s.isActive && ' (inactive)'}
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2 space-y-4">
        {current && (
          <div className="card p-4 bg-blue-50 border-blue-100">
            <p className="text-xs text-gray-500 uppercase font-semibold">Current Monthly Gross</p>
            <p className="text-2xl font-bold text-blue-700">₹{formatRupeesUI(current.monthlyGross)}</p>
            <p className="text-xs text-gray-500 mt-1">effective {formatDateDdMmmYy(current.effectiveFrom)}{Number(current.monthlyTds) > 0 && ` · TDS ₹${formatRupeesUI(current.monthlyTds)} / month`}</p>
          </div>
        )}

        <div className="card p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Revise Salary</h3>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <div>
              <label className="label">Effective From</label>
              <input type="date" className="input-field" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
            </div>
            <div>
              <label className="label">Monthly Gross (₹)</label>
              <input type="number" min={0} className="input-field" value={monthlyGross} onChange={(e) => setMonthlyGross(e.target.value)} />
            </div>
            <div>
              <label className="label">Monthly TDS (₹)</label>
              <input type="number" min={0} className="input-field" value={monthlyTds} onChange={(e) => setMonthlyTds(e.target.value)} placeholder="0 = none" />
            </div>
          </div>
          <label className="label">Notes (optional)</label>
          <input className="input-field mb-2" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. annual increment" />
          {error && <p className="text-red-600 text-sm mb-2">{error}</p>}
          <button className="btn-primary" disabled={saving || !selectedId} onClick={handleAdd}>{saving ? 'Saving…' : 'Add Revision'}</button>
        </div>

        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="table-cell text-left">Effective From</th>
                <th className="table-cell text-right">Monthly Gross</th>
                <th className="table-cell text-right">TDS / month</th>
                <th className="table-cell text-left">Notes</th>
                <th className="table-cell text-left">Added By</th>
                <th className="table-cell text-left"></th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={6} className="table-cell text-center text-gray-400 py-6">No salary on record</td></tr>}
              {history.map((h) => (
                <tr key={h.id} className="border-t border-gray-100">
                  <td className="table-cell">{formatDateDdMmmYy(h.effectiveFrom)}</td>
                  <td className="table-cell text-right">₹{formatRupeesUI(h.monthlyGross)}</td>
                  <td className="table-cell text-right">{Number(h.monthlyTds) > 0 ? `₹${formatRupeesUI(h.monthlyTds)}` : '—'}</td>
                  <td className="table-cell text-gray-500">{h.notes || '—'}</td>
                  <td className="table-cell text-gray-500">{h.createdBy?.staffName || '—'}</td>
                  <td className="table-cell"><button className="text-red-500 text-xs hover:underline" onClick={() => handleDelete(h.id)}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── LEAVE BALANCES ───────────────────────────────────────────────────────────

interface LeaveAdjustment { id: number; year: number; days: number; reason: string; notes?: string | null; createdBy?: { staffName: string } | null }

const LeaveBalancesTab: React.FC = () => {
  const [staffList, setStaffList] = useState<StaffLite[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [rows, setRows] = useState<LeaveAdjustment[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [days, setDays] = useState('');
  const [reason, setReason] = useState('OPENING_BALANCE');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getStaff().then((res) => {
      const list = res.data as StaffLite[];
      setStaffList(list);
      if (list.length > 0) setSelectedId(list[0].id);
    });
  }, []);

  useEffect(() => { if (selectedId) load(selectedId); }, [selectedId]);

  const load = async (staffId: number) => {
    const res = await getLeaveAdjustments(staffId);
    setRows(res.data);
  };

  const handleAdd = async () => {
    if (!selectedId) return;
    setError('');
    const d = Number(days);
    if (!Number.isFinite(d) || d === 0) { setError('Days must be a non-zero number (negative to debit)'); return; }
    setSaving(true);
    try {
      await addLeaveAdjustment(selectedId, { year, days: d, reason, notes: notes || undefined });
      setDays(''); setNotes('');
      load(selectedId);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add adjustment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this leave balance adjustment?')) return;
    await deleteLeaveAdjustment(id);
    if (selectedId) load(selectedId);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="card p-4">
        <h3 className="font-semibold text-gray-800 mb-3">Staff</h3>
        <div className="space-y-1 max-h-[500px] overflow-y-auto">
          {staffList.map((s) => (
            <button key={s.id} onClick={() => setSelectedId(s.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm ${selectedId === s.id ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50 text-gray-700'} ${!s.isActive ? 'opacity-50' : ''}`}>
              {s.staffName}{s.isPartner && ' (Partner)'}{!s.isActive && ' (inactive)'}
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2 space-y-4">
        <div className="card p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Add Opening Balance / Credit / Debit</h3>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <div>
              <label className="label">Year</label>
              <input type="number" className="input-field" value={year} onChange={(e) => setYear(Number(e.target.value))} />
            </div>
            <div>
              <label className="label">Days (± )</label>
              <input type="number" step={0.5} className="input-field" value={days} onChange={(e) => setDays(e.target.value)} placeholder="e.g. 5 or -2" />
            </div>
            <div>
              <label className="label">Reason</label>
              <select className="input-field" value={reason} onChange={(e) => setReason(e.target.value)}>
                <option value="OPENING_BALANCE">Opening Balance</option>
                <option value="CARRY_FORWARD">Carry Forward</option>
                <option value="MANUAL">Manual</option>
              </select>
            </div>
          </div>
          <label className="label">Notes (optional)</label>
          <input className="input-field mb-2" value={notes} onChange={(e) => setNotes(e.target.value)} />
          {error && <p className="text-red-600 text-sm mb-2">{error}</p>}
          <button className="btn-primary" disabled={saving || !selectedId} onClick={handleAdd}>{saving ? 'Saving…' : 'Add'}</button>
        </div>

        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="table-cell text-left">Year</th>
                <th className="table-cell text-right">Days</th>
                <th className="table-cell text-left">Reason</th>
                <th className="table-cell text-left">Notes</th>
                <th className="table-cell text-left"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="table-cell text-center text-gray-400 py-6">No adjustments</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100">
                  <td className="table-cell">{r.year}</td>
                  <td className={`table-cell text-right font-medium ${r.days < 0 ? 'text-red-600' : 'text-green-600'}`}>{r.days > 0 ? '+' : ''}{r.days}</td>
                  <td className="table-cell text-gray-500">{r.reason.replace('_', ' ')}</td>
                  <td className="table-cell text-gray-500">{r.notes || '—'}</td>
                  <td className="table-cell"><button className="text-red-500 text-xs hover:underline" onClick={() => handleDelete(r.id)}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── MY PAYSLIPS ──────────────────────────────────────────────────────────────

interface MyPayslip extends Payslip { run: { id: number; month: number; year: number; finalizedAt: string } }

const MyPayslipsTab: React.FC = () => {
  const [payslips, setPayslips] = useState<MyPayslip[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyPayslips().then((res) => setPayslips(res.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-gray-400 text-sm">Loading…</p>;
  if (payslips.length === 0) return <p className="text-gray-400 text-sm">No finalized payslips yet.</p>;

  return (
    <div className="card p-0 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
          <tr>
            <th className="table-cell text-left">Month</th>
            <th className="table-cell text-right">Payable Days</th>
            <th className="table-cell text-right">LOP Days</th>
            <th className="table-cell text-right">Net Pay</th>
          </tr>
        </thead>
        <tbody>
          {payslips.map((p) => (
            <tr key={p.id} className="border-t border-gray-100">
              <td className="table-cell font-medium">{MONTH_NAMES[p.run.month - 1]} {p.run.year}</td>
              <td className="table-cell text-right">{p.payableDays} / {p.daysInMonth}</td>
              <td className="table-cell text-right">{p.lopDays}</td>
              <td className="table-cell text-right font-semibold">₹{formatRupeesUI(p.netPay)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Payroll;
